import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/leads`);
      setLeads(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load leads. Please try again.');
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-stone-900 mb-2">Contact Form Leads</h1>
            <p className="text-stone-600">All customer inquiries from your website</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-maroon-600 to-maroon-700 text-white">
              <CardHeader>
                <CardTitle className="text-white text-2xl">{leads.length}</CardTitle>
                <CardDescription className="text-maroon-100">Total Leads</CardDescription>
              </CardHeader>
            </Card>
            <Card className="bg-gradient-to-br from-stone-700 to-stone-800 text-white">
              <CardHeader>
                <CardTitle className="text-white text-2xl">
                  {leads.filter(l => new Date(l.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
                </CardTitle>
                <CardDescription className="text-stone-100">Last 7 Days</CardDescription>
              </CardHeader>
            </Card>
            <Card className="bg-gradient-to-br from-amber-700 to-amber-800 text-white">
              <CardHeader>
                <CardTitle className="text-white text-2xl">
                  {leads.filter(l => new Date(l.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)).length}
                </CardTitle>
                <CardDescription className="text-amber-100">Last 24 Hours</CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Leads List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
              <p className="mt-4 text-stone-600">Loading leads...</p>
            </div>
          ) : error ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={fetchLeads}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Retry
                </button>
              </CardContent>
            </Card>
          ) : leads.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <p className="text-stone-600 text-lg">No leads yet. They'll appear here when customers submit the contact form.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <Card key={lead.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-stone-50 to-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl text-stone-900">{lead.name}</CardTitle>
                        <CardDescription className="mt-1">
                          {formatDate(lead.timestamp)}
                        </CardDescription>
                      </div>
                      <div className="text-sm bg-maroon-100 text-maroon-800 px-3 py-1 rounded-full font-medium">
                        New Lead
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold text-stone-700 mb-1">Email</p>
                        <a 
                          href={`mailto:${lead.email}`}
                          className="text-maroon-600 hover:text-maroon-700 hover:underline"
                        >
                          {lead.email}
                        </a>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-stone-700 mb-1">Phone</p>
                        <a 
                          href={`tel:${lead.phone}`}
                          className="text-maroon-600 hover:text-maroon-700 hover:underline"
                        >
                          {lead.phone || 'Not provided'}
                        </a>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-stone-700 mb-2">Message</p>
                      <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                        <p className="text-stone-800 whitespace-pre-wrap">{lead.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
